import { X, Copy, Crosshair } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";

import type { HoverSnapshot } from "./types";

export function HoverDiagnosticsPanel(props: {
  snapshot: HoverSnapshot | null;
  armed: boolean;
  onPick: () => void;
  copyState: "idle" | "success" | "blocked";
  fallbackText: string | null;
  onCapture: () => void;
  onCopy: () => void;
  onClose: () => void;
}) {
  const { snapshot, armed, onPick, copyState, fallbackText, onCapture, onCopy, onClose } = props;

  return (
    <div
      data-hover-diagnostics-root="1"
      className="fixed bottom-4 right-4 z-[1200] w-[360px] max-w-[calc(100vw-2rem)]"
    >
      <Card className="backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <CardHeader className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">Hover Diagnostics</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close diagnostics">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant={armed ? "default" : "outline"} size="sm" onClick={onPick} className="gap-2">
              <Crosshair className="h-4 w-4" />
              {armed ? "Click target…" : "Pick"}
            </Button>

            <Button type="button" variant="outline" size="sm" onClick={onCapture} className="gap-2">
              Capture
            </Button>

            <Button type="button" variant="outline" size="sm" onClick={onCopy} className="gap-2">
              <Copy className="h-4 w-4" />
              Copy
            </Button>

            <span className="text-xs text-muted-foreground">?debugHover=1</span>
          </div>

          <div className="text-xs text-muted-foreground">
            {armed
              ? "Pick is armed — click any element in the app to snapshot it."
              : "Use Pick for exact element capture, or Capture to snapshot the last hovered point."}{" "}
            {copyState === "success" ? "(Copied!)" : copyState === "blocked" ? "(Clipboard blocked — use box below.)" : ""}
          </div>
        </CardHeader>

        <CardContent className="space-y-3 text-sm">
          {!snapshot ? (
            <div className="text-sm text-muted-foreground">Move your pointer to capture a snapshot.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground">(hover:hover)</div>
                  <div className="font-mono">{String(snapshot.matchMedia.hoverHover)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">(pointer:fine)</div>
                  <div className="font-mono">{String(snapshot.matchMedia.pointerFine)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">visibility</div>
                  <div className="font-mono">{snapshot.document.visibilityState}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">hasFocus</div>
                  <div className="font-mono">{String(snapshot.document.hasFocus)}</div>
                </div>
              </div>

              <Separator />

              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">elementFromPoint</div>
                <div className="font-mono text-xs break-words">
                  {snapshot.elementFromPoint
                    ? `${snapshot.elementFromPoint.tag}${snapshot.elementFromPoint.id ? `#${snapshot.elementFromPoint.id}` : ""}`
                    : "(none)"}
                </div>

                <div className="text-xs text-muted-foreground">interactiveTarget (closest)</div>
                <div className="font-mono text-xs break-words">
                  {snapshot.interactiveTarget
                    ? `${snapshot.interactiveTarget.tag}${snapshot.interactiveTarget.id ? `#${snapshot.interactiveTarget.id}` : ""}`
                    : "(none)"}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">element is :hover</div>
                    <div className="font-mono">{String(snapshot.elementFromPointIsHovered)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">interactive is :hover</div>
                    <div className="font-mono">{String(snapshot.interactiveTargetIsHovered)}</div>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground">pointer over overlay</div>
                <div className="font-mono">{String(snapshot.diagnostics.pointerOverOverlay)}</div>

                <div className="text-xs text-muted-foreground">elementsFromPoint</div>
                <div className="font-mono text-xs break-words">
                  {snapshot.elementsFromPoint.length
                    ? snapshot.elementsFromPoint.map((e) => e.tag).join(" → ")
                    : "(empty)"}
                </div>

                <div className="text-xs text-muted-foreground">hoverChain (last)</div>
                <div className="font-mono text-xs break-words">
                  {snapshot.hoverChain.length
                    ? snapshot.hoverChain.map((h) => `${h.tag}${h.id ? `#${h.id}` : ""}`).join(" → ")
                    : "(empty)"}
                </div>
              </div>

              <Separator />

              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">computed (interactiveTarget)</div>
                <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
                  <div>
                    <div className="text-muted-foreground">bg</div>
                    <div className="break-words">{snapshot.computed.interactiveTarget?.backgroundColor ?? "(n/a)"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">color</div>
                    <div className="break-words">{snapshot.computed.interactiveTarget?.color ?? "(n/a)"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">shadow</div>
                    <div className="break-words">{snapshot.computed.interactiveTarget?.boxShadow ?? "(n/a)"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">transform</div>
                    <div className="break-words">{snapshot.computed.interactiveTarget?.transform ?? "(n/a)"}</div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground">dialog[open]</div>
                  <div className="font-mono">{snapshot.dialogs.openNativeDialogs}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">body scroll locked</div>
                  <div className="font-mono">{String(snapshot.dialogs.bodyScrollLocked)}</div>
                </div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground">activeElement</div>
                <div className="font-mono text-xs break-words">{snapshot.focus.activeElement}</div>
              </div>

              {fallbackText ? (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">Manual copy (clipboard blocked)</div>
                    <Textarea value={fallbackText} readOnly className="h-32 font-mono text-xs" />
                    <div className="text-xs text-muted-foreground">Select all and copy from the box above.</div>
                  </div>
                </>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
